import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import panelApi from "@/lib/panelApi";
import { useAcenteAuth } from "@/context/AcenteAuthContext";
import { Input } from "@/components/ui/input";
import { Plus, Search, Eye, Download, Printer, Building2, Trash2, Undo2, X, ListChecks, User, Sparkles } from "lucide-react";
import { exportCsv, printView } from "@/lib/exportUtils";
import { exportBrandedPdf } from "@/lib/pdfExport";
import { FileText } from "lucide-react";
import { formatTr } from "@/lib/date";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const MEAL_LABELS = { kahvalti: "Kahvaltı", ogle: "Öğle", aksam: "Akşam" };

export default function AcenteReservations() {
  const { user } = useAcenteAuth();
  const [items, setItems] = useState([]);
  const [agencyMap, setAgencyMap] = useState({});
  const [q, setQ] = useState("");
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    panelApi
      .get("/reservations")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    // Only admin/operasyon can list agencies. Regular acente/ozel_acente
    // users see just their own agency name from the /me response, so we
    // skip the call entirely for them to avoid noisy 403 console errors.
    if (["admin", "operasyon", "rezervasyon"].includes(user?.role)) {
      panelApi
        .get("/agencies")
        .then((r) => setAgencyMap(Object.fromEntries(r.data.map((a) => [a.id, a.name]))))
        .catch(() => {});
    } else if (user?.agency_id && user?.agency_name) {
      setAgencyMap({ [user.agency_id]: user.agency_name });
    }
  }, [user?.agency_id, user?.agency_name, user?.role]);

  const filtered = useMemo(() => {
    let arr = [...items];
    if (q) {
      const needle = q.toLowerCase();
      arr = arr.filter(
        (r) =>
          r.reservation_number?.toLowerCase().includes(needle) ||
          r.group_name?.toLowerCase().includes(needle) ||
          r.hotel_name?.toLowerCase().includes(needle) ||
          r.customer_phone?.toLowerCase().includes(needle) ||
          (agencyMap[r.agency_id] || "").toLowerCase().includes(needle)
      );
    }
    // Newest → oldest by creation timestamp (fallback to reservation_number
    // which is prefixed with the YYYYMMDD creation date).
    arr.sort((a, b) => {
      const av = a.created_at || a.reservation_number || "";
      const bv = b.created_at || b.reservation_number || "";
      return bv.localeCompare(av);
    });
    return arr;
  }, [items, q, agencyMap]);

  const canCreate = ["acente", "ozel_acente", "admin", "operasyon", "rezervasyon"].includes(user?.role);
  // Bulk delete + 1-minute undo is admin-only. This intentionally bypasses the
  // acente revision flow — admins are the safety valve, not the source of edits.
  const canBulkDelete = user?.role === "admin";
  // Any role that can do at least one bulk action gets the checkbox column.
  const canSelect = canBulkDelete;
  // Monthly Excel export — for agencies who need to hand a sales list to
  // their accountant. Staff already have the analytics tools; this is a
  // simplified one-click PDF/Excel option for the sales side.
  const canMonthlyExport = ["acente", "ozel_acente"].includes(user?.role);

  // -------------------- Monthly Excel export state --------------------
  const [monthOpen, setMonthOpen] = useState(false);
  const [monthValue, setMonthValue] = useState(() => new Date().toISOString().slice(0, 7));

  // Deep-link support: /acente/rezervasyonlar?export=1 opens the monthly
  // Excel modal right away — dashboard shortcut for özel acente uses this.
  useEffect(() => {
    if (canMonthlyExport && searchParams.get("export") === "1") {
      setMonthOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canMonthlyExport]);

  const runMonthlyExport = async () => {
    try {
      // Import lazily so the xlsx library isn't loaded until the user asks
      // for the export (keeps the initial bundle lighter for staff roles).
      const { exportReservationsMonthlyExcel } = await import("@/lib/monthlyExcelExport");
      const r = exportReservationsMonthlyExcel(items, monthValue, {
        agencyName: user?.agency_name || "",
      });
      if (r.count === 0) {
        toast.error("Seçilen ayda rezervasyon yok");
        return;
      }
      toast.success(`${r.count} rezervasyon indirildi — ${r.filename}`);
      setMonthOpen(false);
    } catch (err) {
      toast.error("Excel oluşturulamadı");
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  // -------------------- Bulk select + soft-delete state --------------------
  // Set of reservation ids currently checked. Cleared after successful delete
  // or when the filter/search changes to prevent stale selections.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  // The trash bar tracks the most recent bulk-delete so admins can undo it
  // within a 60-second window. undoState = { ids, expiresAt, restored }.
  const [undoState, setUndoState] = useState(null);
  const undoTimerRef = useRef(null);
  const [now, setNow] = useState(Date.now());

  // Tick every second while the undo bar is visible so the countdown updates.
  useEffect(() => {
    if (!undoState) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [undoState]);

  // Reset selection when the visible list changes (search switch).
  useEffect(() => { setSelectedIds(new Set()); }, [q]);

  const clearSelection = () => setSelectedIds(new Set());
  const toggleId = (id, on) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  };
  const toggleAllVisible = (on) => {
    setSelectedIds(() => (on ? new Set(filtered.map((r) => r.id)) : new Set()));
  };

  const runBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const r = await panelApi.post("/reservations/bulk-delete", { ids });
      const soft = r?.data?.soft_deleted ?? ids.length;
      // Optimistically remove from the visible list.
      setItems((prev) => prev.filter((x) => !selectedIds.has(x.id)));
      clearSelection();
      // Start the undo window.
      const expiresAt = Date.now() + 60_000;
      setUndoState({ ids, expiresAt, restored: false });
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      undoTimerRef.current = setTimeout(() => setUndoState(null), 60_500);
      toast.success(`${soft} rezervasyon silindi. 60 saniye içinde geri alabilirsiniz.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Silinemedi");
    } finally {
      setBulkBusy(false);
      setConfirmOpen(false);
    }
  };

  const runUndo = async () => {
    if (!undoState || undoState.restored) return;
    try {
      await panelApi.post("/reservations/bulk-restore", { ids: undoState.ids });
      // Re-fetch the list so the restored rows reappear in their original place.
      const r = await panelApi.get("/reservations");
      setItems(r.data);
      setUndoState((s) => (s ? { ...s, restored: true } : s));
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      setTimeout(() => setUndoState(null), 2500);
      toast.success("Rezervasyonlar geri getirildi");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Geri alınamadı");
    }
  };
  const remainingSec = undoState ? Math.max(0, Math.ceil((undoState.expiresAt - now) / 1000)) : 0;

  // -------------------- Bulk delete handlers --------------------
  const [actionBusy, setActionBusy] = useState(false);

  const exportCols = [
    { key: "reservation_number", label: "Numara" },
    { key: "agency_name", label: "Acente", value: (r) => agencyMap[r.agency_id] || "-" },
    { key: "group_name", label: "Grup" },
    { key: "checkin", label: "Giriş" },
    { key: "checkout", label: "Çıkış" },
    { key: "nights", label: "Gece" },
    { key: "adults", label: "Yetişkin" },
    { key: "children", label: "Çocuk" },
    { key: "staff", label: "Görevli" },
  ];
  const exportName = `rezervasyonlar-${new Date().toISOString().slice(0, 10)}`;

  return (
    <div className="space-y-6" data-testid="acente-reservations-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2">Rezervasyonlar</div>
          <h1 className="font-serif text-3xl text-brand-navy">Rezervasyon Takibi</h1>
          <p className="text-sm text-slate-600 mt-1">Tüm rezervasyonlar tek listede — arama kutusuyla hızlıca filtreleyin.</p>
        </div>
        {canCreate && (
          <Link
            to="/acente/rezervasyon/yeni"
            className="inline-flex items-center gap-2 rounded-full bg-brand-orange hover:bg-brand-orange-dark text-white px-5 py-2.5 font-medium"
            data-testid="acente-new-reservation-cta"
          >
            <Plus size={16} /> Yeni Rezervasyon
          </Link>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-brand-navy/10 p-4">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="text-xs text-slate-500">
            Toplam <b className="text-brand-navy">{filtered.length}</b> rezervasyon
          </div>
          <div className="relative w-full sm:w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Numara / Grup / Otel / Telefon / Acente ara..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" data-testid="acente-reservations-search" />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2 justify-end">
          {canMonthlyExport && (
            <button
              onClick={() => setMonthOpen(true)}
              data-testid="reservations-monthly-excel-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 px-3 py-1.5 text-xs font-medium shadow-sm"
            >
              <Download size={12} /> Aylık Excel
            </button>
          )}
          <button
            onClick={() => exportCsv(exportName, exportCols, filtered)}
            disabled={filtered.length === 0}
            data-testid="reservations-export-csv"
            className="inline-flex items-center gap-1.5 rounded-full border border-brand-navy/20 px-3 py-1.5 text-xs hover:bg-brand-navy/5 disabled:opacity-40"
          >
            <Download size={12} /> CSV / Excel
          </button>
          <button
            onClick={() => printView("Rezervasyonlar", exportCols, filtered)}
            disabled={filtered.length === 0}
            data-testid="reservations-export-print"
            className="inline-flex items-center gap-1.5 rounded-full border border-brand-navy/20 px-3 py-1.5 text-xs hover:bg-brand-navy/5 disabled:opacity-40"
          >
            <Printer size={12} /> Yazdır
          </button>
          <button
            onClick={() => exportBrandedPdf({
              title: "Rezervasyon Listesi",
              subtitle: user?.agency_name || "Tüm Acenteler",
              columns: exportCols,
              rows: filtered,
              meta: {
                "Toplam Kayıt": filtered.length,
                "Acente": user?.agency_name || "—",
                "Tarih": new Date().toLocaleDateString("tr-TR"),
              },
              footerNote: "Bu belge Ariaf ASIS Acente Paneli tarafından oluşturulmuştur. Rezervasyon detayları için lütfen sisteme giriş yapınız.",
            })}
            disabled={filtered.length === 0}
            data-testid="reservations-export-pdf"
            className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-brand-navy to-brand-navy/90 text-white px-3 py-1.5 text-xs hover:from-brand-orange-dark hover:to-brand-orange disabled:opacity-40"
          >
            <FileText size={12} /> PDF Oluştur
          </button>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="acente-reservations-table">
            <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
              <tr className="text-left">
                {canSelect && (
                  <th className="py-2 pl-2 pr-1 w-8">
                    <input
                      type="checkbox"
                      checked={filtered.length > 0 && filtered.every((r) => selectedIds.has(r.id))}
                      ref={(el) => {
                        if (el) {
                          const some = filtered.some((r) => selectedIds.has(r.id));
                          const all = filtered.length > 0 && filtered.every((r) => selectedIds.has(r.id));
                          el.indeterminate = some && !all;
                        }
                      }}
                      onChange={(e) => toggleAllVisible(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-brand-orange focus:ring-brand-orange cursor-pointer"
                      title={filtered.every((r) => selectedIds.has(r.id)) ? "Seçimi kaldır" : "Tümünü seç"}
                      data-testid="reservations-select-all"
                    />
                  </th>
                )}
                <th className="py-2 px-2">Numara</th>
                <th className="py-2 px-2">Acente / Grup</th>
                <th className="py-2 px-2">Giriş</th>
                <th className="py-2 px-2">Çıkış</th>
                <th className="py-2 px-2">Gece</th>
                <th className="py-2 px-2">Kişi (Y/Ç/G) &<br/>Öğün</th>
                <th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={canSelect ? 8 : 7} className="py-6 text-center text-slate-500">Yükleniyor...</td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={canSelect ? 8 : 7} className="py-6 text-center text-slate-500">
                    Bu kriterlere uygun rezervasyon bulunamadı.
                  </td>
                </tr>
              )}
              {filtered.map((r) => {
                const agencyName = agencyMap[r.agency_id];
                const checked = selectedIds.has(r.id);
                // Iter 26: özel_acente creates B2C reservations under their
                // own name — display the individual seller's name in the
                // Acente column instead of the auto-agency name. Falls back
                // to the agency name for regular acente users.
                const isOzelRow = r.created_by_role === "ozel_acente";
                const displayName = isOzelRow
                  ? (r.created_by_name || agencyName || "")
                  : agencyName;
                return (
                  <tr
                    key={r.id}
                    className={`border-b border-slate-100 hover:bg-slate-50 ${checked ? "bg-orange-50/60" : ""}`}
                    data-testid={`reservation-row-${r.id}`}
                  >
                    {canSelect && (
                      <td className="py-3 pl-2 pr-1 align-top">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => toggleId(r.id, e.target.checked)}
                          className="w-4 h-4 rounded border-slate-300 text-brand-orange focus:ring-brand-orange cursor-pointer"
                          data-testid={`reservation-select-${r.id}`}
                        />
                      </td>
                    )}
                    <td className="py-3 px-2 font-mono text-xs align-top">
                      <div>{r.reservation_number}</div>
                      {r.is_revised && (
                        <div
                          className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-200"
                          title={r.revised_at ? `Revize: ${new Date(r.revised_at).toLocaleString("tr-TR")}` : "Revize edildi"}
                          data-testid={`reservation-revised-${r.id}`}
                        >
                          <Sparkles size={9}/> Revize
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-2 align-top">
                      <div className="flex items-center gap-1.5 font-medium text-brand-navy" data-testid={`reservation-agency-${r.id}`}>
                        {displayName ? (
                          <>
                            {isOzelRow ? (
                              <User size={12} className="text-emerald-600" />
                            ) : (
                              <Building2 size={12} className="text-brand-orange" />
                            )}
                            {displayName}
                            {isOzelRow && (
                              <span className="ml-1 text-[9px] uppercase tracking-widest text-emerald-700 bg-emerald-100 rounded-full px-1.5 py-0.5" data-testid={`reservation-ozel-badge-${r.id}`}>
                                Özel
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-slate-400 italic">Acente bilgisi yok</span>
                        )}
                      </div>
                      {r.group_name && (
                        <div className="text-xs text-slate-500 mt-0.5" data-testid={`reservation-group-${r.id}`}>{r.group_name}</div>
                      )}
                      {r.customer_phone && (
                        <div className="text-xs text-slate-500 mt-0.5" data-testid={`reservation-phone-${r.id}`}>📞 {r.customer_phone}</div>
                      )}
                      {r.hotel_name && (
                        <div className="text-[11px] text-brand-navy/70 mt-0.5 inline-flex items-center gap-1" data-testid={`reservation-hotel-${r.id}`}>
                          <span className="rounded-full bg-brand-navy/5 border border-brand-navy/10 px-2 py-0.5">{r.hotel_name}</span>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-2 align-top">{formatTr(r.checkin)}</td>
                    <td className="py-3 px-2 align-top">{formatTr(r.checkout)}</td>
                    <td className="py-3 px-2 align-top">{r.nights}</td>
                    <td className="py-3 px-2 text-xs align-top">
                      <div className="text-slate-700 font-mono">{r.adults} / {r.children} / {r.staff}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5" data-testid={`reservation-meals-${r.id}`}>
                        {MEAL_LABELS[r.meal_start] || r.meal_start || "—"} → {MEAL_LABELS[r.meal_end] || r.meal_end || "—"}
                      </div>
                    </td>
                    <td className="py-3 px-2 align-top">
                      <Link to={`/acente/rezervasyon/${r.id}`} data-testid={`reservation-view-${r.id}`} className="inline-flex items-center gap-1 text-brand-orange hover:text-brand-orange-dark text-xs">
                        <Eye size={14} /> Detay
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {/* Bulk action bar — floats sticky on top of the list once anything is
          selected. Shows Sil button. Admin only. */}
      {canSelect && selectedIds.size > 0 && (
        <div
          className="sticky top-4 z-30 rounded-2xl bg-brand-navy text-white shadow-xl border border-brand-navy/40 px-4 py-3 flex items-center gap-4 flex-wrap"
          data-testid="reservations-bulk-action-bar"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
              <ListChecks size={18}/>
            </div>
            <div>
              <div className="text-sm font-medium">
                <span data-testid="reservations-selected-count">{selectedIds.size}</span> rezervasyon seçildi
              </div>
              <div className="text-[11px] text-white/60">Silmek için aşağıdaki butonu kullanın.</div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
            <button
              type="button"
              onClick={clearSelection}
              className="rounded-full bg-white/10 hover:bg-white/20 text-white text-xs px-3 py-1.5 inline-flex items-center gap-1"
              data-testid="reservations-bulk-clear"
            >
              <X size={12}/> Vazgeç
            </button>
            {canBulkDelete && (
              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                className="rounded-full bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold px-4 py-2 inline-flex items-center gap-1 shadow-lg shadow-rose-500/30"
                data-testid="reservations-bulk-delete-btn"
              >
                <Trash2 size={14}/> Sil ({selectedIds.size})
              </button>
            )}
          </div>
        </div>
      )}

      {/* Undo bar — floats after a bulk-delete, gives a 60s window to
          reverse it. When the countdown hits 0 the toast vanishes and the
          backend eventually purges the rows for good. */}
      {undoState && !undoState.restored && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-2xl bg-brand-navy text-white shadow-2xl border border-brand-orange/40 px-5 py-4 flex items-center gap-4 max-w-[92vw]"
          data-testid="reservations-undo-toast"
        >
          <div className="w-11 h-11 rounded-xl bg-emerald-500/20 text-emerald-300 flex items-center justify-center flex-shrink-0">
            <Trash2 size={20}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold" data-testid="reservations-undo-count">
              {undoState.ids.length} rezervasyon silindi
            </div>
            <div className="text-[11px] text-white/70" data-testid="reservations-undo-countdown">
              Kalıcı silinmesine {remainingSec} saniye kaldı
            </div>
          </div>
          <button
            type="button"
            onClick={runUndo}
            className="rounded-full bg-brand-orange hover:bg-brand-orange-dark text-white font-semibold text-xs px-4 py-2 inline-flex items-center gap-1.5 shadow-md"
            data-testid="reservations-undo-btn"
          >
            <Undo2 size={13}/> Geri Al
          </button>
        </div>
      )}

      <AlertDialog open={monthOpen} onOpenChange={setMonthOpen}>
        <AlertDialogContent data-testid="reservations-monthly-excel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Aylık Satış Raporu (Excel)</AlertDialogTitle>
            <AlertDialogDescription>
              Muhasebeciniz için hazır bir Excel dosyası indirin. Seçtiğiniz aya ait
              giriş tarihi olan tüm rezervasyonlar rapora dahil edilir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 space-y-3">
            <label className="block text-sm font-medium text-emerald-900">Ay Seçin</label>
            <Input
              type="month"
              value={monthValue}
              onChange={(e) => setMonthValue(e.target.value)}
              className="bg-white"
              data-testid="reservations-monthly-excel-month"
            />
            <div className="text-xs text-emerald-800/80">
              Dosya adı: <b>{(user?.agency_name || "acente").replace(/[\\/:*?"<>|]+/g, "_")}_{monthValue}_satis-raporu.xlsx</b>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reservations-monthly-excel-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={runMonthlyExport}
              className="bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-600"
              data-testid="reservations-monthly-excel-download"
            >
              İndir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="reservations-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Seçilen rezervasyonları sil</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{selectedIds.size}</b> rezervasyon silinecek. Bu işlem 60 saniye içinde
              "Geri Al" ile iptal edilebilir; 60 saniye sonra kalıcı silinir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reservations-bulk-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={runBulkDelete}
              disabled={bulkBusy}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
              data-testid="reservations-bulk-confirm"
            >
              {bulkBusy ? "Siliniyor..." : "Evet, Sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
